import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
export function SavingsCalculator() {
  const [target, setTarget] = useState<number>(100000);
  const [initial, setInitial] = useState<number>(10000);
  const [monthly, setMonthly] = useState<number>(1000);
  const [rate, setRate] = useState<number>(4.5);
  const results = useMemo(() => {
    const t = target || 0;
    const p = initial || 0;
    const pmt = monthly || 0;
    const r = (rate || 0) / 100 / 12;
    if (t <= p || pmt <= 0 || r <= 0) {
      return {
        monthsToGoal: 0,
        totalSaved: p,
        interestEarned: 0,
        chartData: []
      };
    }
    let currentBalance = p;
    let months = 0;
    let totalContributions = p;
    const chartData = [{
      month: 0,
      balance: p,
      contributions: p
    }];
    // Cap at 600 months (50 years) to prevent infinite loops
    while (currentBalance < t && months < 600) {
      months++;
      const interest = currentBalance * r;
      currentBalance += interest + pmt;
      totalContributions += pmt;
      if (months % 12 === 0 || currentBalance >= t) {
        chartData.push({
          month: months,
          balance: Math.round(currentBalance),
          contributions: Math.round(totalContributions)
        });
      }
    }
    return {
      monthsToGoal: months,
      totalSaved: currentBalance,
      interestEarned: currentBalance - totalContributions,
      chartData
    };
  }, [target, initial, monthly, rate]);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(val);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-439">
      <div className="lg:col-span-1 space-y-6 bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-440">
        <h3 className="text-xl font-semibold text-white mb-4" data-id="element-441">Savings Goal</h3>

        <div data-id="element-442">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-443">
            Target Amount ($)
          </label>
          <input type="number" value={target} onChange={e => setTarget(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" data-id="element-444" />
        </div>

        <div data-id="element-445">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-446">
            Initial Deposit ($)
          </label>
          <input type="number" value={initial} onChange={e => setInitial(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" data-id="element-447" />
        </div>

        <div data-id="element-448">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-449">
            Monthly Contribution ($)
          </label>
          <input type="number" value={monthly} onChange={e => setMonthly(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" data-id="element-450" />
        </div>

        <div data-id="element-451">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-452">
            Interest Rate (p.a. %)
          </label>
          <input type="number" step="0.1" value={rate} onChange={e => setRate(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" data-id="element-453" />
        </div>
      </div>

      <div className="lg:col-span-2 space-y-6" data-id="element-454">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-455">
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-456">
            <p className="text-sm text-slate-400 mb-1" data-id="element-457">Time to Goal</p>
            <p className="text-3xl font-bold text-teal-400" data-id="element-458">
              {Math.floor(results.monthsToGoal / 12)}y{' '}
              {results.monthsToGoal % 12}m
            </p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-459">
            <p className="text-sm text-slate-400 mb-1" data-id="element-460">Total Saved</p>
            <p className="text-3xl font-bold text-white" data-id="element-461">
              {formatCurrency(results.totalSaved)}
            </p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-462">
            <p className="text-sm text-slate-400 mb-1" data-id="element-463">Interest Earned</p>
            <p className="text-3xl font-bold text-cyan-400" data-id="element-464">
              {formatCurrency(results.interestEarned)}
            </p>
          </div>
        </div>

        <div className="bg-white/5 p-6 rounded-2xl border border-white/10 h-[400px]" data-id="element-465">
          <h4 className="text-sm font-medium text-slate-400 mb-6" data-id="element-466">
            Growth Projection
          </h4>
          <ResponsiveContainer width="100%" height="100%" data-id="element-467">
            <LineChart data={results.chartData} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0
          }} data-id="element-468">
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-469" />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `${Math.floor(val / 12)}y`} data-id="element-470" />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `$${val / 1000}k`} data-id="element-471" />
              <Tooltip contentStyle={{
              backgroundColor: '#0f172a',
              borderColor: '#1e293b',
              borderRadius: '8px',
              color: '#fff'
            }} formatter={(value: number) => formatCurrency(value)} labelFormatter={label => `Month ${label}`} data-id="element-472" />
              <Legend wrapperStyle={{
              paddingTop: '20px'
            }} data-id="element-473" />
              <Line type="monotone" dataKey="balance" name="Total Balance" stroke="#14b8a6" strokeWidth={3} dot={false} data-id="element-474" />
              <Line type="monotone" dataKey="contributions" name="Total Contributions" stroke="#3b82f6" strokeWidth={3} strokeDasharray="5 5" dot={false} data-id="element-475" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}